import { getAccessToken, getUser, isSupabaseAuthConfigured, refreshSession } from './supabaseAuth';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

async function apiGetAuth(path) {
  const doRequest = async (token) => {
    const response = await fetch(`${SUPABASE_URL}${path}`, {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`
      }
    });

    const text = await response.text();
    if (!response.ok) {
      const err = new Error(text || 'Request failed');
      err.status = response.status;
      throw err;
    }
    return text ? JSON.parse(text) : null;
  };

  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  try {
    return await doRequest(token);
  } catch (e) {
    if (e && Number(e.status) === 401) {
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) throw e;
      return doRequest(refreshed.access_token);
    }
    throw e;
  }
}

async function apiUpsertAuth(path, body) {
  const doRequest = async (token) => {
    const response = await fetch(`${SUPABASE_URL}${path}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation,resolution=merge-duplicates'
      },
      body: JSON.stringify(body)
    });

    const text = await response.text();
    if (!response.ok) {
      const err = new Error(text || 'Update failed');
      err.status = response.status;
      throw err;
    }
    return text ? JSON.parse(text) : null;
  };

  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  try {
    return await doRequest(token);
  } catch (e) {
    if (e && Number(e.status) === 401) {
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) throw e;
      return doRequest(refreshed.access_token);
    }
    throw e;
  }
}

async function apiRpcAuth(path, payload = {}) {
  const doRequest = async (token) => {
    const response = await fetch(`${SUPABASE_URL}${path}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload || {})
    });

    const text = await response.text();
    if (!response.ok) {
      const err = new Error(text || 'RPC failed');
      err.status = response.status;
      throw err;
    }
    return text ? JSON.parse(text) : null;
  };

  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  try {
    return await doRequest(token);
  } catch (e) {
    if (e && Number(e.status) === 401) {
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) throw e;
      return doRequest(refreshed.access_token);
    }
    throw e;
  }
}

async function upsertProfileRow(userId, payload) {
  try {
    return await apiUpsertAuth('/rest/v1/profiles?on_conflict=id', [{ id: userId, ...payload }]);
  } catch (e) {
    // Backward compatibility for schemas without updated_at.
    const message = String(e?.message || '');
    if (payload.updated_at && message.includes('updated_at')) {
      const { updated_at: _ignored, ...withoutUpdatedAt } = payload;
      return apiUpsertAuth('/rest/v1/profiles?on_conflict=id', [{ id: userId, ...withoutUpdatedAt }]);
    }
    throw e;
  }
}

async function computeMyIqPointsFallback(userId) {
  let lessonPoints = 0;
  let puzzlePoints = 0;

  try {
    const rewards = await apiGetAuth(
      `/rest/v1/user_lesson_rewards?user_id=eq.${userId}&select=points_awarded&limit=10000`
    );
    lessonPoints = Array.isArray(rewards)
      ? rewards.reduce((sum, r) => sum + Number(r?.points_awarded || 0), 0)
      : 0;
  } catch {
    lessonPoints = 0;
  }

  try {
    const stats = await apiRpcAuth('/rest/v1/rpc/get_puzzle_user_panel_stats', {});
    puzzlePoints = Number(
      stats?.puzzle_points_total ??
      stats?.puzzlePoints ??
      0
    );
  } catch {
    puzzlePoints = 0;
  }

  return Math.max(0, Math.round(Number(lessonPoints || 0) + Number(puzzlePoints || 0)));
}

export function computeUserLevelFromEloPoints(eloPoints) {
  return computeMastersPathFromEloPoints(eloPoints).level;
}

export function computeMastersPathFromEloPoints(eloPoints) {
  const pts = Math.max(0, Number(eloPoints || 0));

  // The Master's Path (Calea Maestrului).
  // Levels 1-10 use hand-tuned thresholds. After Level 10, we use a steady slope so we can scale
  // with future point sources (puzzles, articles, etc.).
  const base = [
    { level: 1, required: 0 },
    { level: 2, required: 10 },
    { level: 3, required: 25 },
    { level: 4, required: 45 },
    { level: 5, required: 70 },
    { level: 6, required: 100 },
    { level: 7, required: 140 },
    { level: 8, required: 190 },
    { level: 9, required: 250 },
    { level: 10, required: 320 }
  ];

  const afterLevel10Step = 50; // +50 IQ Points per level after Level 10.

  const rankForLevel = (lvl) => {
    // Explorer: 1-3, Apprentice: 4-6
    if (lvl <= 3) return 'explorer';
    if (lvl <= 6) return 'apprentice';

    // Strategist spans 7 levels (7-13)
    if (lvl <= 13) return 'strategist';
    // Champion spans 9 levels (14-22)
    if (lvl <= 22) return 'champion';
    // Master spans 10 levels (23-32)
    if (lvl <= 32) return 'master';
    // Legend: 33+
    return 'legend';
  };

  let level = 1;
  let currentRequired = 0;
  let nextRequired = null;

  if (pts < base[base.length - 1].required) {
    for (let i = 0; i < base.length; i++) {
      if (pts >= base[i].required) {
        level = base[i].level;
        currentRequired = base[i].required;
        nextRequired = base[i + 1] ? base[i + 1].required : null;
      }
    }
  } else {
    const extra = Math.floor((pts - base[base.length - 1].required) / afterLevel10Step);
    level = 10 + extra;
    currentRequired = base[base.length - 1].required + extra * afterLevel10Step;
    nextRequired = currentRequired + afterLevel10Step;
  }

  const rankKey = rankForLevel(level);
  const toNext = nextRequired == null ? 0 : Math.max(0, nextRequired - pts);
  const progress =
    nextRequired == null || nextRequired <= currentRequired
      ? 1
      : Math.min(1, Math.max(0, (pts - currentRequired) / (nextRequired - currentRequired)));

  return {
    points: pts,
    level,
    rankKey,
    currentRequired,
    nextRequired,
    pointsToNext: toNext,
    progressToNext: progress,
    isMaxDefinedLevel: false
  };
}

export async function fetchMyProfile() {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;

  // Note: RLS should allow selecting own row.
  try {
    // Keep select schema-agnostic: some environments may not have all optional columns.
    const rows = await apiGetAuth(
      `/rest/v1/profiles?id=eq.${user.id}&select=*&limit=1`
    );
    const p = rows && rows[0];
    if (!p) return null;

    const nickname = (p.nickname || p.display_name || '').trim();
    let eloPoints = Number(p.elo_points || 0);

    // Self-heal IQ points if profile.elo_points lags behind rewards/puzzle points.
    const rebuiltIq = await computeMyIqPointsFallback(user.id);
    if (rebuiltIq > eloPoints) {
      eloPoints = rebuiltIq;
      try {
        await upsertProfileRow(user.id, {
          elo_points: rebuiltIq,
          updated_at: new Date().toISOString()
        });
      } catch {
        // If upsert fails, still expose rebuilt value in UI.
      }
    }

    const mastersPath = computeMastersPathFromEloPoints(eloPoints);
    return {
      ...p,
      nickname,
      eloPoints,
      computedLevel: mastersPath.level,
      computedRankKey: mastersPath.rankKey,
      mastersPath
    };
  } catch {
    // Fallback: if profiles RLS isn't configured yet, at least expose nickname from auth metadata.
    const rebuiltIq = await computeMyIqPointsFallback(user.id);
    const metaNickname = String(user?.user_metadata?.nickname || user?.user_metadata?.display_name || '').trim();
    return {
      id: user.id,
      nickname: metaNickname,
      eloPoints: rebuiltIq,
      computedLevel: computeMastersPathFromEloPoints(rebuiltIq).level,
      computedRankKey: computeMastersPathFromEloPoints(rebuiltIq).rankKey,
      mastersPath: computeMastersPathFromEloPoints(rebuiltIq)
    };
  }
}

export async function upsertMyNickname(nickname) {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;

  const clean = String(nickname || '').trim();
  if (!clean) throw new Error('Nickname is required');

  const rows = await upsertProfileRow(user.id, {
    nickname: clean,
    updated_at: new Date().toISOString()
  });
  return rows && rows[0] ? rows[0] : null;
}

export async function fetchMyLessonReward(lessonDbId) {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;
  if (!lessonDbId) return null;

  const rows = await apiGetAuth(
    `/rest/v1/user_lesson_rewards?user_id=eq.${user.id}&lesson_id=eq.${lessonDbId}` +
      `&select=points_awarded,multiplier,awarded_at,base_points&limit=1`
  );
  return rows && rows[0] ? rows[0] : null;
}

export async function applyMyIqPointsDelta(delta) {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;

  const d = Number(delta || 0);
  if (!Number.isFinite(d) || d === 0) return null;

  const currentRows = await apiGetAuth(
    `/rest/v1/profiles?id=eq.${user.id}&select=id,elo_points&limit=1`
  );
  const current = currentRows && currentRows[0] ? Number(currentRows[0].elo_points || 0) : 0;
  const next = Math.max(0, Math.round(current + d));

  const updated = await upsertProfileRow(user.id, {
    elo_points: next,
    updated_at: new Date().toISOString()
  });
  return updated && updated[0] ? updated[0] : { id: user.id, elo_points: next };
}
